/**
 * The Revit Database panel — the old web's `RevitIndexSidebar` in its
 * `embedded` mode: it takes over the chat rail while open. Two levels, not a
 * tree: the document's type keys (localised, with counts), then one type's
 * items behind a back chevron; typing in the search box asks the agent's
 * search across every type instead (debounced 250 ms). A row is a button that
 * is both clickable — insert at the caret — and draggable into the composer
 * (`REVIT_INDEX_MIME`, `copy`).
 *
 * The data is `useDocumentContext`'s. The status the old panel took from a
 * WebView2 push (building / failed / stale / missing) comes here from the
 * plugin's status file, read through the agent and polled while the panel is
 * open (item BK). Nothing is built unasked: "No index yet" offers **Build
 * index**, a changed model shows the stale banner, and the header's refresh
 * asks Revit to rebuild — the one indexing cost, on the user's click. An old
 * plugin with no status gets the file re-read instead.
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import type { DocumentContextState } from "../../hooks/useDocumentContext";
import type { DocumentContextItem, DocumentContextStatus } from "../../lib/documentContext";
import { REVIT_INDEX_MIME, toDocumentContextMentionSource, type DocumentContextMentionSource } from "../../lib/documentContextMention";
import { resolveIndexView, type IndexViewKind } from "../../lib/revitIndexState";
import { useLocale } from "../../i18n/LocaleProvider";

export const INDEX_SEARCH_DEBOUNCE_MS = 250;
/** How often the panel asks whether the document changed since the build — a small local file read. */
export const STATUS_POLL_MS = 3000;

interface IndexRow {
  type: string;
  item: DocumentContextItem;
}

function formatUpdated(iso: string | null, locale: string): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat(locale === "zh" ? "zh-TW" : "en", { dateStyle: "medium", timeStyle: "short" }).format(date);
}

export function RevitIndexPanel({
  context,
  onInsert,
  onClose,
}: {
  context: DocumentContextState;
  onInsert: (source: DocumentContextMentionSource) => void;
  onClose: () => void;
}) {
  const { t, locale } = useLocale();
  const [query, setQuery] = useState("");
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [rows, setRows] = useState<IndexRow[]>([]);
  const [loadingRows, setLoadingRows] = useState(false);
  const requestRef = useRef(0);
  const asideRef = useRef<HTMLElement>(null);

  const canInsert = context.canBrowseDocumentContext;
  const { displayNames, keys, keyCounts, searchItems, fetchTypeItems, refresh, fetchStatus, rebuild } = context;

  const resetNavigation = useCallback(() => {
    requestRef.current += 1;
    setQuery("");
    setSelectedType(null);
    setRows([]);
    setLoadingRows(false);
  }, []);

  // A different instance or document is a different index: start over.
  useEffect(() => {
    resetNavigation();
  }, [context.revitVersion, context.documentName, resetNavigation]);

  useEffect(() => {
    requestAnimationFrame(() => asideRef.current?.focus());
  }, []);

  // "The document changed since the build" arrives by asking, not by a push
  // (item BK): the plugin writes its status beside the context and does no
  // other work; this panel reads it while open.
  useEffect(() => {
    void fetchStatus();
    const id = window.setInterval(() => void fetchStatus(), STATUS_POLL_MS);
    return () => window.clearInterval(id);
  }, [fetchStatus]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // The search box: the agent's search across every type, debounced.
  useEffect(() => {
    const trimmed = query.trim();
    if (!trimmed) return;
    const request = ++requestRef.current;
    setLoadingRows(true);
    const timeout = window.setTimeout(async () => {
      const hits = await searchItems(trimmed);
      if (request !== requestRef.current) return;
      setRows(hits.map((hit) => ({ type: hit.type, item: hit as unknown as DocumentContextItem })));
      setLoadingRows(false);
    }, INDEX_SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timeout);
  }, [query, searchItems]);

  const openType = async (type: string) => {
    const request = ++requestRef.current;
    setSelectedType(type);
    setQuery("");
    setLoadingRows(true);
    try {
      const items = await fetchTypeItems(type);
      if (request === requestRef.current) setRows(items.map((item) => ({ type, item })));
    } finally {
      if (request === requestRef.current) setLoadingRows(false);
    }
  };

  const insert = (row: IndexRow) => {
    if (!canInsert) return;
    onInsert(toDocumentContextMentionSource(row.type, row.item));
  };

  const startDrag = (event: React.DragEvent, row: IndexRow) => {
    if (!canInsert) {
      event.preventDefault();
      return;
    }
    event.dataTransfer.setData(REVIT_INDEX_MIME, JSON.stringify(toDocumentContextMentionSource(row.type, row.item)));
    event.dataTransfer.effectAllowed = "copy";
  };

  const title = selectedType ? (displayNames[selectedType] ?? selectedType) : t("revitIndex.title");
  const updated = formatUpdated(context.lastUpdated, locale);
  const showRows = query.trim().length > 0 || selectedType !== null;
  const view = resolveIndexView({
    status: context.status,
    keysLoaded: context.keysLoaded,
    keyCount: keys.length,
    loading: context.loadingKeys || loadingRows,
    showRows,
    rebuilding: context.rebuilding,
  });
  // Refresh asks Revit to rebuild when the plugin can; an old plugin (no
  // status) gets the file re-read, which is all it can offer.
  const canRebuild = context.status?.canRebuild === true;
  const rebuildOrRefresh = () => {
    resetNavigation();
    if (canRebuild) void rebuild();
    else void refresh();
  };

  let body: React.ReactNode;
  if (view.kind !== "rows" && view.kind !== "types") {
    body = <IndexStatusView kind={view.kind} status={context.status} onAction={rebuildOrRefresh} />;
  } else if (view.kind === "rows") {
    body =
      rows.length === 0 ? (
        <p className="revit-index-empty">{t("revitIndex.noResults")}</p>
      ) : (
        rows.map((row) => (
          <button
            key={`${row.type}-${row.item.id}`}
            type="button"
            className={`revit-index-row${canInsert ? " is-draggable" : ""}`}
            disabled={!canInsert}
            draggable={canInsert}
            onDragStart={(event) => startDrag(event, row)}
            onClick={() => insert(row)}
            title={canInsert ? t("revitIndex.dragHint") : undefined}
          >
            {canInsert ? <GripIcon /> : null}
            <span className="revit-index-row-main">
              <span className="revit-index-row-name">{row.item.name}</span>
              <span className="revit-index-row-type">{displayNames[row.type] ?? row.type}</span>
            </span>
            {canInsert ? <PlusIcon /> : null}
          </button>
        ))
      );
  } else {
    body = keys.map((key) => (
      <button key={key} type="button" className="revit-index-type" onClick={() => void openType(key)}>
        <span className="revit-index-type-name">{displayNames[key] ?? key}</span>
        <span className="revit-index-type-meta">
          {typeof keyCounts[key] === "number" ? (
            <span className="revit-index-count">{keyCounts[key]!.toLocaleString(locale === "zh" ? "zh-TW" : "en")}</span>
          ) : null}
          <ChevronRightIcon />
        </span>
      </button>
    ));
  }

  return (
    <aside ref={asideRef} className="revit-index" role="complementary" tabIndex={-1} aria-label={t("revitIndex.title")}>
      <header className="revit-index-head">
        {selectedType ? (
          <button type="button" className="icon-btn" onClick={resetNavigation} aria-label={t("revitIndex.back")} title={t("revitIndex.back")}>
            <ChevronLeftIcon />
          </button>
        ) : null}
        <span className="revit-index-badge" aria-hidden="true">
          <DatabaseIcon size={18} />
        </span>
        <div className="revit-index-title">
          <h2>{title}</h2>
          {context.documentName ? <p title={context.documentName}>{context.documentName}</p> : null}
        </div>
        <button
          type="button"
          className={`icon-btn${view.stale ? " is-attention" : ""}`}
          onClick={rebuildOrRefresh}
          disabled={view.kind === "building" || context.rebuilding}
          aria-label={t("revitIndex.refresh")}
          title={t("revitIndex.refresh")}
        >
          <RefreshIcon />
        </button>
        <button type="button" className="icon-btn" onClick={onClose} aria-label={t("revitIndex.close")} title={t("revitIndex.close")}>
          <CloseIcon />
        </button>
      </header>

      {canInsert ? (
        <div className="revit-index-search">
          <SearchIcon />
          <input
            type="search"
            value={query}
            placeholder={t("revitIndex.searchPlaceholder")}
            aria-label={t("revitIndex.searchPlaceholder")}
            onChange={(event) => {
              const next = event.target.value;
              setQuery(next);
              setSelectedType(null);
              if (!next.trim()) {
                requestRef.current += 1;
                setRows([]);
                setLoadingRows(false);
              }
            }}
          />
        </div>
      ) : null}

      {view.stale ? (
        <div className="revit-index-banner is-stale" role="status">
          <AlertIcon />
          <span>{t("revitIndex.staleWarning")}</span>
        </div>
      ) : null}

      <div className="revit-index-scroll">
        <div className="revit-index-list">{body}</div>
      </div>

      {updated ? <p className="revit-index-foot">{t("revitIndex.updated").replace("{time}", updated)}</p> : null}
    </aside>
  );
}

/**
 * What the index shows while it is not browsable — building, loading, failed, no
 * document, nothing built — with the one action each offers. Shared with anything
 * else that needs the index (the work settings form), so they read the same.
 */
export function IndexStatusView({ kind, status, onAction }: {
  kind: Exclude<IndexViewKind, "rows" | "types">;
  status: DocumentContextStatus | null;
  onAction: () => void;
}) {
  const { t } = useLocale();
  if (kind === "building") return <LoadingState label={t("revitIndex.building")} />;
  if (kind === "loading") return <LoadingState label={t("revitIndex.loading")} />;
  if (kind === "failed")
    return <ActionState title={t("revitIndex.failed")} description={status?.error ?? t("revitIndex.failedDescription")} action={t("revitIndex.retry")} onAction={onAction} />;
  if (kind === "unavailable")
    return <ActionState title={t("revitIndex.unavailable")} description={t("revitIndex.unavailableDescription")} />;
  return <ActionState title={t("revitIndex.missing")} description={t("revitIndex.missingDescription")}
    action={status?.canRebuild ? t("revitIndex.build") : t("revitIndex.refresh")} onAction={onAction} />;
}

function LoadingState({ label }: { label: string }) {
  return (
    <div className="revit-index-loading" role="status">
      <p>{label}</p>
      {[0, 1, 2, 3].map((i) => (
        <span key={i} className="revit-index-skeleton" />
      ))}
    </div>
  );
}

export function ActionState({ title, description, action, onAction, icon }: { title: string; description: string; action?: string; onAction?: () => void; icon?: ReactNode }) {
  return (
    <div className="revit-index-state">
      {icon ?? <DatabaseIcon size={32} />}
      <h3>{title}</h3>
      <p>{description}</p>
      {action && onAction ? (
        <button type="button" className="settings-btn secondary is-small" onClick={onAction}>
          {action}
        </button>
      ) : null}
    </div>
  );
}

/** lucide `Database`. */
export function DatabaseIcon({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <ellipse cx="12" cy="5" rx="9" ry="3" />
      <path d="M3 5V19A9 3 0 0 0 21 19V5" />
      <path d="M3 12A9 3 0 0 0 21 12" />
    </svg>
  );
}

/** lucide `AlertTriangle`. */
function AlertIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3" />
      <path d="M12 9v4" />
      <path d="M12 17h.01" />
    </svg>
  );
}

function ChevronLeftIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="m15 18-6-6 6-6" />
    </svg>
  );
}

function ChevronRightIcon() {
  return (
    <svg className="revit-index-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="m9 18 6-6-6-6" />
    </svg>
  );
}

function RefreshIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
      <path d="M21 3v5h-5" />
      <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
      <path d="M8 16H3v5" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M18 6 6 18" />
      <path d="m6 6 12 12" />
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg className="revit-index-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.3-4.3" />
    </svg>
  );
}

function GripIcon() {
  return (
    <svg className="revit-index-grip" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="9" cy="12" r="1" />
      <circle cx="9" cy="5" r="1" />
      <circle cx="9" cy="19" r="1" />
      <circle cx="15" cy="12" r="1" />
      <circle cx="15" cy="5" r="1" />
      <circle cx="15" cy="19" r="1" />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg className="revit-index-plus" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M5 12h14" />
      <path d="M12 5v14" />
    </svg>
  );
}
